import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Boxes, Megaphone, FileText, Loader2, ShoppingCart, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import SectionToggle from "../SectionToggle";
// Feb-2026 Iter 28: ImageGridSection wird jetzt direkt in ExtraTechnicalSection eingebunden.
import AdReachSection from "../AdReachSection";
import SocialCaptionsButton from "../SocialCaptionsButton";
import { useBasicsCollapse } from "../BasicsCollapseContext";
import PortalTransactionSection from "../PortalTransactionSection";
import PriceFieldsBlock from "../PriceFieldsBlock";
import ListingActionButtons from "../ListingActionButtons";
import DispositionActions from "../DispositionActions";
import ListingProtocol from "../ListingProtocol";
import { Button } from "../../ui/button";
import api from "../../../lib/api";
import { previewPdfResponse } from "../../../lib/pdfPreview";

/**
 * FooterActionsSection — the bottom half of CreateListingPage:
 *  - Existing-listing action row (edit mode only)
 *  - Bilder & Video (collapsible)
 *  - Werbung & Reichweite (collapsible, dealer-only)
 *  - Disposition / Portal-Transaktion (collapsible, dealer-only)
 *  - eBay-Sync toggle (dealer-only, only if eBay app_id configured)
 *  - Submit / Vorschau / Cancel / „Als Entwurf speichern" buttons
 *  - Protokoll (edit mode only)
 *
 * Pure presentational — receives form + setters + flags from the parent.
 */
export default function FooterActionsSection({
    form,
    setForm,
    update,
    user,
    isDealer,
    isEdit,
    id,
    submitting,
    isDraftMode,
    saveDraftNow,
    setPreviewOpen,
    navigate,
    essentialOnly = false,
    orderMode = false,
    dispoMode = false,
    beforePriceSlot = null,
}) {
    const { t } = useTranslation();
    const { closeBasicsIfOpen } = useBasicsCollapse();
    // Mai-2026 (User-Wunsch 21 May Iteration 10) — Ankaufvertrag-Generator.
    // Speichert die aktuellen Formular-Daten zusätzlich als Entwurf (resume später)
    // und lädt einen unterschriftsbereiten Ankaufvertrag als PDF herunter.
    const [ankaufLoading, setAnkaufLoading] = useState(false);
    const generateAnkauf = async () => {
        if (!form.brand || !form.model) {
            toast.error("Bitte mindestens Marke und Modell ausfüllen, bevor ein Ankaufvertrag erstellt wird.");
            return;
        }
        setAnkaufLoading(true);
        try {
            // Entwurf parallel speichern, damit der Händler später dort fortfahren kann
            try { await saveDraftNow?.(); } catch { /* noop */ }
            const res = await api.post("/listings/ankaufvertrag-pdf", { form }, { responseType: "blob" });
            const safeBrand = (form.brand || "Fahrzeug").replace(/[^\w-]+/g, "-");
            const safeModel = (form.model || "").replace(/[^\w-]+/g, "-");
            previewPdfResponse(res, `Ankaufvertrag-${safeBrand}-${safeModel}.pdf`.replace(/--+/g, "-"));
            toast.success("Ankaufvertrag erstellt — bitte ausdrucken und unterschreiben lassen.");
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Ankaufvertrag konnte nicht erstellt werden.");
        } finally {
            setAnkaufLoading(false);
        }
    };
    return (
        <>
            {/* Jul-2026 (User-Wunsch) — „Zustand & Schadensermittlung" wird direkt
                ÜBER „Preise & Vermittlungsprovision" eingeblendet. */}
            {beforePriceSlot}

            {/* Feb-2026 (User-Wunsch) — „Inserat-Aktionen" wurde zwischen
                „Preise & Vermittlungsprovision" und „Werbung & Reichweite"
                verschoben (siehe unten). */}

            {/* Feb-2026 (User-Wunsch 24 May Iter 27) — Preise & Vermittlungs-
                provision direkt unter „Bilder & Video". Aus BasicsSection
                extrahiert. */}
            <PriceFieldsBlock
                form={form}
                update={update}
                isDealer={isDealer}
                orderMode={orderMode}
                isAuction={(form.sale_type || "fixed_price") === "auction"}
                listingId={isEdit ? id : null}
            />

            {/* Existing-listing aktions (edit mode only) — jetzt zwischen Preise
                und Werbung & Reichweite. */}
            {isEdit && id && (
                <ListingActionButtons
                    listingId={id}
                    currentStatus={form.status || "active"}
                    soldFlag={Boolean(form.sold)}
                    saleType={form.sale_type}
                    sharingEnabled={form.sharing_enabled}
                    listingLabel={[form.brand, form.model, form.year ? `EZ ${form.year}` : null].filter(Boolean).join(" · ") || form.title}
                    onAfterChange={async (data) => {
                        if (data?.status) {
                            setForm((p) => ({ ...p, status: data.status }));
                        }
                        // Feb-2026 (User-Wunsch 26 May): Nach Verkauf/Reservierung
                        // NICHT automatisch zurück zum Dashboard navigieren — der
                        // Händler will häufig direkt im Anschluss in die
                        // Disposition (Belege, Versand) wechseln.
                        if (data && data.soft_delete) {
                            setTimeout(() => navigate("/dashboard"), 800);
                        }
                    }}
                    onDuplicated={(newId) => {
                        window.open(`/edit-listing/${newId}`, "_blank", "noopener,noreferrer");
                    }}
                />
            )}

            {/* Werbung & Reichweite (Dealer only) */}
            {isDealer && !essentialOnly && (
                <SectionToggle
                    title="Werbung & Reichweite"
                    icon={Megaphone}
                    hint="Top-Inserat, Börsen-Versand, Social-Media-Posting"
                    testId="ad-reach-section-toggle"
                    headerExtras={<SocialCaptionsButton form={form} />}
                    onOpenChange={(v) => { if (v) closeBasicsIfOpen(); }}
                >
                    <AdReachSection form={form} update={update} user={user} listingId={isEdit ? id : null} />
                </SectionToggle>
            )}

            {/* Jul-2026 (User-Wunsch) — in ZWEI Aufklappfelder getrennt:
                „Disposition" (Fahrzeugdaten, Papiere, Schlüssel, Standort) und
                „Verkaufsabwicklung" (Fortschritt, Kaufvertrag, Belege …).
                Jul-2026: auch für Händler-MITARBEITER sichtbar (Backend erlaubt
                Dispo-Zugriff via map_zweirad_employee — z. B. Rabatt-Freigabe). */}
            {(isDealer || user?.role === "admin" || (user?.role === "zweiradverkaeufer" && user?.employer_dealer_id)) && !essentialOnly && (
                <SectionToggle
                    title="Disposition"
                    titleExtra={form.title || [form.brand, form.model].filter(Boolean).join(" ")}
                    icon={Boxes}
                    testId="disposition-section-toggle"
                    defaultOpen={dispoMode}
                    onOpenChange={(v) => { if (v) closeBasicsIfOpen(); }}
                    headerExtras={isEdit && form.internal_number && (
                        <span
                            className="font-mono-num text-[11px] font-bold px-2.5 py-1 rounded-sm bg-[#111827] text-[#FFCC00] tracking-wider whitespace-nowrap"
                            title="Einmalige und unveränderliche Angebotsnummer"
                            data-testid="disposition-internal-number-badge"
                        >
                            Nr. {form.internal_number}
                        </span>
                    )}
                >
                    <PortalTransactionSection form={form} update={update} listingId={isEdit ? id : null} user={user} hideActions />
                </SectionToggle>
            )}
            {(isDealer || user?.role === "admin" || (user?.role === "zweiradverkaeufer" && user?.employer_dealer_id)) && !essentialOnly && isEdit && id && (
                <SectionToggle
                    title="Verkaufsabwicklung"
                    titleExtra={form.title || [form.brand, form.model].filter(Boolean).join(" ")}
                    icon={ClipboardList}
                    testId="verkaufsabwicklung-section-toggle"
                    defaultOpen={dispoMode}
                    hint="Abwicklungs-Fortschritt, Kaufvertrag, Belege, Transport, Auslieferung"
                    onOpenChange={(v) => { if (v) closeBasicsIfOpen(); }}
                >
                    <div className="bg-white border-2 border-emerald-200 rounded-sm p-6">
                        <DispositionActions listingId={id} condition={form.condition} />
                    </div>
                </SectionToggle>
            )}

            {/* eBay-Sync toggle (only when configured) */}
            {isDealer && user.ebay_app_id && !essentialOnly && (
                <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="ebay-sync-toggle-section">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!form.ebay_sync_enabled}
                            onChange={(e) => update("ebay_sync_enabled", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#111827]"
                            data-testid="ebay-sync-toggle"
                        />
                        <div>
                            <div className="font-bold text-[#111827]">An eBay übergeben</div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                Markiert dieses Inserat für den eBay-Sync (siehe Warenwirtschaft → eBay).
                            </div>
                        </div>
                    </label>
                </div>
            )}

            {/* Submit / Vorschau / Cancel / Draft.
                Feb-2026 (User-Wunsch): Im Disposition-Modus ausgeblendet — diese
                Zeile wird nur beim Fahrzeug-Inserieren benötigt. */}
            {!dispoMode && (
            <div className="flex flex-wrap gap-3 justify-end" data-testid="publish-section">
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    className="rounded-sm"
                    data-testid="cancel-listing-btn"
                >
                    {t("common.cancel")}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPreviewOpen(true)}
                    className="rounded-sm border-[#111827] text-[#111827] font-bold uppercase tracking-wider"
                    data-testid="preview-listing-btn"
                >
                    Vorschau
                </Button>
                {isDraftMode ? (
                    <Button
                        type="button"
                        onClick={saveDraftNow}
                        className="bg-[#FFCC00] hover:bg-yellow-300 text-[#111827] font-bold rounded-sm px-8"
                        data-testid="save-draft-btn"
                        title="Inserat speichern und später fortsetzen"
                    >
                        💾 Als Entwurf speichern
                    </Button>
                ) : (
                    <Button
                        type="submit"
                        disabled={submitting}
                        className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm px-8"
                        data-testid="submit-listing-btn"
                    >
                        {submitting
                            ? t("buy_request.sending")
                            : isEdit
                                ? t("create_listing.submit_edit")
                                : t("create_listing.submit_new")}
                    </Button>
                )}
                {/* Mai-2026 (User-Wunsch 21 May Iteration 10) — Ankaufvertrag-PDF rechts neben dem Draft-/Submit-Button (Händler only). Jul-2026: im Bestellmodus (Fahrzeug verkaufen) ausgeblendet. */}
                {isDealer && !orderMode && (
                    <Button
                        type="button"
                        onClick={generateAnkauf}
                        disabled={ankaufLoading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-sm px-6"
                        data-testid="ankauf-create-btn"
                        title="Verbindlichen Kfz-Ankaufvertrag als PDF generieren (mit Händlerdaten + Logo + allen Fahrzeugfeldern + Unterschriftsfeldern)."
                    >
                        {ankaufLoading
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Erzeuge PDF…</>
                            : <><FileText className="w-4 h-4 mr-2" /> Ankauf erstellen</>}
                    </Button>
                )}
            </div>
            )}

            {/* Protokoll (edit mode only) */}
            {isEdit && id && <ListingProtocol listingId={id} />}
        </>
    );
}
